import { useState, useEffect, useMemo } from 'react'
import DOMPurify from 'dompurify'
import { X, Calendar, Eye, FileText, Archive, User, Tag } from 'lucide-react'
import type { Post } from '@/services/postsService'
import usersService from '@/services/usersService'

interface PostDetailsModalProps {
  isOpen: boolean
  onClose: () => void
  post: Post | null
}

export function PostDetailsModal({ isOpen, onClose, post }: PostDetailsModalProps) {
  const [authorName, setAuthorName] = useState<string | null>(null)

  useEffect(() => {
    if (!post?.author_id) return
    setAuthorName(null)
    usersService.getUserById(post.author_id)
      .then(user => setAuthorName(user.name))
      .catch(() => setAuthorName(null))
  }, [post?.author_id])

  const sanitizedContent = useMemo(
    () => (post?.content ? DOMPurify.sanitize(post.content) : ''),
    [post?.content]
  )

  if (!isOpen || !post) return null

  const getStatusInfo = (status: string) => {
    const statusMap = {
      published: { label: 'Publicado', color: 'text-green-600', bg: 'bg-green-50', icon: Eye },
      draft: { label: 'Rascunho', color: 'text-yellow-600', bg: 'bg-yellow-50', icon: FileText },
      archived: { label: 'Arquivado', color: 'text-gray-600', bg: 'bg-gray-50', icon: Archive },
    }
    return statusMap[status as keyof typeof statusMap] || statusMap.draft
  }

  const statusInfo = getStatusInfo(post.status)
  const StatusIcon = statusInfo.icon

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b sticky top-0 bg-white z-10">
          <h2 className="text-xl font-semibold">Detalhes do Post</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Imagem de Capa */}
          {post.cover_image_url && (
            <div className="flex justify-center">
              <img
                src={post.cover_image_url}
                alt={post.title}
                className="max-h-80 rounded-lg object-cover shadow-md w-full"
                onError={(e) => {
                  e.currentTarget.src = 'https://via.placeholder.com/800x400?text=Sem+Imagem'
                }}
              />
            </div>
          )}

          {/* Status */}
          <div className={`${statusInfo.bg} ${statusInfo.color} px-4 py-3 rounded-lg flex items-center gap-2`}>
            <StatusIcon className="h-5 w-5" />
            <span className="font-medium">Status: {statusInfo.label}</span>
          </div>

          {/* Título e Slug */}
          <div>
            <h3 className="text-2xl font-bold text-gray-900 mb-2">{post.title}</h3>
            {post.slug && (
              <div className="flex items-center gap-2 text-sm text-gray-500">
                <code className="bg-gray-100 px-2 py-1 rounded font-mono">/{post.slug}</code>
              </div>
            )}
          </div>

          {/* Resumo */}
          {post.summary && (
            <div>
              <h4 className="text-lg font-semibold mb-2 text-gray-900">Resumo</h4>
              <p className="text-gray-700 leading-relaxed">{post.summary}</p>
            </div>
          )}

          {/* Conteúdo */}
          {post.content && (
            <div>
              <h4 className="text-lg font-semibold mb-2 text-gray-900">Conteúdo</h4>
              <div className="prose max-w-none">
                <div
                  className="text-gray-700 leading-relaxed whitespace-pre-wrap"
                  dangerouslySetInnerHTML={{ __html: sanitizedContent }}
                />
              </div>
            </div>
          )}

          {/* Taxonomias (Tags/Categorias) */}
          {post.taxonomies && post.taxonomies.length > 0 && (
            <div>
              <h4 className="text-lg font-semibold mb-3 text-gray-900">Tags e Categorias</h4>
              <div className="flex flex-wrap gap-2">
                {post.taxonomies.map((tax) => (
                  <div
                    key={tax.id}
                    className="flex items-center gap-1 bg-blue-50 text-blue-700 px-3 py-1 rounded-full text-sm"
                  >
                    <Tag className="h-3 w-3" />
                    <span>{tax.name}</span>
                    <span className="text-xs text-blue-500">({tax.type})</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Métricas */}
          <div>
            <h4 className="text-lg font-semibold mb-3 text-gray-900">Métricas</h4>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              <div className="bg-gray-50 p-3 rounded-lg">
                <p className="text-sm text-gray-500">Visualizações</p>
                <p className="text-2xl font-bold text-gray-900">{post.view_count}</p>
              </div>
              {post.author_id && (
                <div className="bg-gray-50 p-3 rounded-lg">
                  <p className="text-sm text-gray-500 flex items-center gap-1">
                    <User className="h-3 w-3" />
                    Autor
                  </p>
                  <p className="text-sm font-medium text-gray-900">
                    {authorName ?? '...'}
                  </p>
                  <p className="text-xs font-mono text-gray-400 truncate mt-0.5">{post.author_id}</p>
                </div>
              )}
            </div>
          </div>

          {/* Metadata */}
          {post.metadata && Object.keys(post.metadata).length > 0 && (
            <div>
              <h4 className="text-lg font-semibold mb-3 text-gray-900">Metadata</h4>
              <div className="bg-gray-50 p-4 rounded-lg">
                <pre className="text-xs text-gray-700 overflow-x-auto">
                  {JSON.stringify(post.metadata, null, 2)}
                </pre>
              </div>
            </div>
          )}

          {/* Datas */}
          <div>
            <h4 className="text-lg font-semibold mb-3 text-gray-900">Informações do Sistema</h4>
            <div className="space-y-2 text-sm">
              <div className="flex items-center gap-2">
                <Calendar className="h-4 w-4 text-gray-400" />
                <span className="text-gray-500">Criado em:</span>
                <span className="text-gray-900">
                  {new Date(post.created_at).toLocaleDateString('pt-BR', {
                    day: '2-digit',
                    month: '2-digit',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </span>
              </div>
              {post.updated_at && (
                <div className="flex items-center gap-2">
                  <Calendar className="h-4 w-4 text-gray-400" />
                  <span className="text-gray-500">Última atualização:</span>
                  <span className="text-gray-900">
                    {new Date(post.updated_at).toLocaleDateString('pt-BR', {
                      day: '2-digit',
                      month: '2-digit',
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </span>
                </div>
              )}
              {post.published_at && (
                <div className="flex items-center gap-2">
                  <Calendar className="h-4 w-4 text-gray-400" />
                  <span className="text-gray-500">Publicado em:</span>
                  <span className="text-gray-900">
                    {new Date(post.published_at).toLocaleDateString('pt-BR', {
                      day: '2-digit',
                      month: '2-digit',
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-3 p-6 border-t bg-gray-50">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700"
          >
            Fechar
          </button>
        </div>
      </div>
    </div>
  )
}
